<template>
  <div id="promptViewModal" class="modal fade" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-body p-0">
          <div class="d-flex align-items-center p-3 border-bottom">
            <div class="flex-grow-1">
              <div class="fw-bold fs-5 mb-1">{{ title }}</div>
              <div class="d-flex align-items-center gap-2 text-muted small">
                <span v-if="featureType" class="badge bg-secondary" style="font-size: 0.8em;">{{ featureType }}</span>
                <span v-if="version">v{{ version }}</span>
                <span class="badge" :class="isActive ? 'bg-success' : 'bg-danger'">{{ isActive ? 'Active' : 'Inactive' }}</span>
                <span class="ms-2">Created: {{ createdAt }}</span>
                <span v-if="content" class="badge bg-info" style="font-size: 0.8em;">Chars: {{ characterCount }} | Tokens: {{ tokenCount }}</span>
              </div>
            </div>
            <div class="d-flex align-items-center gap-2">
              <button 
                v-if="content" 
                class="btn btn-outline-secondary btn-sm" 
                @click="copyContent"
                :disabled="copying"
              >
                <span v-if="copying" class="spinner-border spinner-border-sm me-1" role="status"></span>
                {{ copying ? 'Copying...' : 'Copy' }}
              </button>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
          </div>

          <div v-if="loading" class="d-flex flex-column align-items-center justify-content-center p-5" style="min-height: 300px;">
            <div class="spinner-border text-primary mb-3" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <div class="text-muted">Loading full prompt content...</div>
          </div>

          <div v-else class="p-4" style="max-height: 60vh; overflow-y: auto;">
            <!-- System Prompt Layout -->
            <template v-if="type === 'system'">
              <div class="bg-light p-3 rounded markdown-content" v-html="formattedContent"></div>
            </template>
            <!-- Organization Prompt Layout -->
            <template v-else-if="type === 'organization'">
              <div class="bg-light p-3 rounded markdown-content" v-html="formattedContent"></div>
            </template>
            <!-- Fragment Prompt Layout -->
            <template v-else-if="type === 'fragment'">
              <div v-if="description" class="mb-4">
                <div class="fw-bold mb-1">Description</div>
                <div class="bg-light p-3 rounded markdown-content" v-html="parseMarkdown(description)"></div>
              </div>
              <div>
                <div class="fw-bold mb-1">Prompt Content</div>
                <div class="bg-light p-3 rounded markdown-content" v-html="formattedContent"></div>
              </div>
            </template>
            <!-- Suggested Prompt Layout (fallback) -->
            <template v-else>
              <div v-if="description" class="mb-4">
                <div class="fw-bold mb-1">Description</div>
                <div class="bg-light p-3 rounded markdown-content" v-html="parseMarkdown(description)"></div>
              </div>
              <div>
                <div class="fw-bold mb-1">Prompt Content</div>
                <div class="bg-light p-3 rounded markdown-content" v-html="formattedContent"></div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { encode } from 'gpt-tokenizer'
import { parseMarkdown } from '@/utils/markdown.js'

export default {
  name: 'PromptViewModal',
  props: {
    show: Boolean,
    loading: Boolean,
    type: String, // 'system', 'suggested', 'organization', or 'fragment'
    title: String,
    featureType: String,
    version: String,
    order: [String, Number],
    isActive: Boolean,
    createdAt: String,
    content: String,
    description: String,
  },
  computed: {
    characterCount() {
      return this.content ? this.content.length : 0;
    },
    tokenCount() {
      if (!this.content) return 0;
      try {
        return encode(this.content).length;
      } catch (error) {
        console.error('Error encoding tokens:', error);
        return 0;
      }
    },
    formattedContent() {
      if (!this.content) return '';
      return this.parseMarkdown(this.content);
    }
  },
  data() {
    return {
      model: streetmetrics.model,
      currentModal: null,
      modalElement: null,
      copying: false,
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.openModal();
      } else {
        this.closeModal();
      }
    }
  },
  mounted() {
    this.modalElement = document.getElementById('promptViewModal')
    this.currentModal = new bootstrap.Modal(this.modalElement)
    if (this.show) {
      this.currentModal.show()
    }
    this.modalElement.addEventListener('hidden.bs.modal', () => {
      this.$emit('close')
    })
  },
  beforeDestroy() {
    this.modalElement.removeEventListener('hidden.bs.modal')
  },
  methods: {
    openModal() {
      this.currentModal.show()
    },
    closeModal() {
      this.currentModal.hide()
    },
    async copyContent() {
      if (!this.content) return;
      try {
        this.copying = true;
        await navigator.clipboard.writeText(this.content);
        this.model.toast.background = 'bg-green';
        this.model.toast.message = 'Content copied to clipboard!';
      } catch (err) {
        console.error('Failed to copy content: ', err);
        this.model.toast.background = 'bg-orange';
        this.model.toast.message = 'Failed to copy content.';
      } finally {
        this.copying = false;
      }
    },
    parseMarkdown(text) {
      return parseMarkdown(text);
    }
  }
}
</script>

<style>
@import '../assets/css/markdown.scss';

/* Modal-specific overrides */
#promptViewModal .markdown-content h1 {
  font-size: 1rem !important;
}

#promptViewModal .markdown-content h2 {
  font-size: 0.9375rem !important;
}

#promptViewModal .markdown-content h3 {
  font-size: 0.875rem !important;
}

#promptViewModal .markdown-content h4 {
  font-size: 0.8125rem !important;
}

#promptViewModal .markdown-content h5 {
  font-size: 0.75rem !important;
}

#promptViewModal .markdown-content h6 {
  font-size: 0.6875rem !important;
}
</style> 